const padDatePart = n => String(n).padStart(2, '0')

export function dateArrayToInput (arr) {
  if (!Array.isArray(arr) || arr.length < 3) return ''
  const [day, month, year] = arr
  if (!day || !month || !year) return ''
  return `${year}-${padDatePart(month)}-${padDatePart(day)}`
}

export function dateArrayToDisplay (arr) {
  if (!Array.isArray(arr) || arr.length < 3) return ''
  const [day, month, year] = arr
  if (!day || !month || !year) return ''
  return `${padDatePart(day)}-${padDatePart(month)}-${year}`
}

export function dateInputToArray (value) {
  if (!value) return []
  const parts = value.split('-').map(Number)
  if (parts.length < 3) return []
  const [year, month, day] = parts
  return [day, month, year]
}

function toLocalDay (arr) {
  if (!Array.isArray(arr) || arr.length < 3) return null
  const [day, month, year] = arr
  if (!day || !month || !year) return null
  return new Date(year, month - 1, day)
}

/** Schedule on site by startDate/endDate vs today */
export function getCategoryScheduleStatus (category, now = new Date()) {
  const start = toLocalDay(category.startDate)
  const end = toLocalDay(category.endDate)
  if (!start || !end) {
    return { key: 'live', label: 'Live', color: 'green' }
  }
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  if (today < start) {
    return { key: 'scheduled', label: 'Scheduled', color: 'blue' }
  }
  if (today > end) {
    return { key: 'expired', label: 'Expired', color: 'orange' }
  }
  return { key: 'live', label: 'Live now', color: 'green' }
}

export function inferHasPromo (raw) {
  if (typeof raw?.hasPromo === 'boolean') return raw.hasPromo
  const excludes = raw?.promoBannerExcludes || raw?.eventPromoBannerExcludes || []
  if (excludes.length > 0) return true
  const promo = raw?.promoBanner || raw?.eventPromoBanner || {}
  return !!(
    promo.customEventGameThumb ||
    promo.promoThumbPath ||
    Object.values(promo.titleLocales || {}).some(v => v && String(v).trim())
  )
}

function normalizeDomainList (raw) {
  if (!Array.isArray(raw)) return []
  return raw.map(domain => String(domain).trim()).filter(Boolean)
}

function normalizeCategoryExcludes (raw) {
  return normalizeDomainList(raw?.excludes)
}

function coerceLegacyDates (dates) {
  if (!Array.isArray(dates) || dates.length === 0) return []
  const first = dates[0]
  if (Array.isArray(first) && first.length >= 3) return first
  if (typeof first === 'string' && first.includes('-')) {
    return dateInputToArray(first)
  }
  return []
}

function migrateLegacyCategory (entry) {
  const ensure = (obj, key, fallback) => (obj && typeof obj[key] !== 'undefined') ? obj[key] : fallback
  const promo = entry.eventPromoBanner || entry.eventPromoBannerTURNOFF || {}
  return {
    id: ensure(entry, 'id', 'seasonal-games'),
    isCategory: true,
    name: ensure(entry, 'eventName', ''),
    nameLocales: ensure(entry, 'eventNameLocales', {}),
    description: ensure(entry, 'eventDescription', ''),
    descriptionLocales: ensure(entry, 'eventDescriptionLocales', {}),
    order: ensure(entry, 'eventCategoryOrder', []),
    startDate: coerceLegacyDates(entry.startDate),
    endDate: coerceLegacyDates(entry.endDate),
    promoBanner: {
      customEventGameThumb: ensure(promo, 'customEventGameThumb', ''),
      customEventGameThumbWebp: ensure(promo, 'customEventGameThumbWebp', ''),
      promoThumbPath: ensure(promo, 'promoThumbPath', ''),
      promoThumbPathWebP: ensure(promo, 'promoThumbPathWebP', ''),
      titleLocales: ensure(promo, 'titleLocales', {}),
      subtitleLocales: ensure(promo, 'subtitleLocales', {}),
      buttonLocales: ensure(promo, 'buttonLocales', {})
    },
    hasPromo: inferHasPromo(entry),
    promoBannerExcludes: ensure(entry, 'eventPromoBannerExcludes', []),
    excludes: normalizeCategoryExcludes(entry),
    games: ensure(entry, 'eventGames', [])
  }
}

export function normalizeCategoryFromApi (raw) {
  const ensure = (obj, key, fallback) => (obj && typeof obj[key] !== 'undefined') ? obj[key] : fallback
  const promo = raw.promoBanner || raw.eventPromoBanner || {}
  return {
    id: ensure(raw, 'id', ''),
    isCategory: true,
    name: ensure(raw, 'name', ensure(raw, 'eventName', '')),
    nameLocales: ensure(raw, 'nameLocales', ensure(raw, 'eventNameLocales', {})),
    description: ensure(raw, 'description', ensure(raw, 'eventDescription', '')),
    descriptionLocales: ensure(raw, 'descriptionLocales', ensure(raw, 'eventDescriptionLocales', {})),
    order: ensure(raw, 'order', ensure(raw, 'eventCategoryOrder', [])),
    startDate: Array.isArray(raw.startDate) ? raw.startDate.slice() : [],
    endDate: Array.isArray(raw.endDate) ? raw.endDate.slice() : [],
    promoBanner: {
      customEventGameThumb: ensure(promo, 'customEventGameThumb', ''),
      customEventGameThumbWebp: ensure(promo, 'customEventGameThumbWebp', ''),
      promoThumbPath: ensure(promo, 'promoThumbPath', ''),
      promoThumbPathWebP: ensure(promo, 'promoThumbPathWebP', ''),
      titleLocales: ensure(promo, 'titleLocales', {}),
      subtitleLocales: ensure(promo, 'subtitleLocales', {}),
      buttonLocales: ensure(promo, 'buttonLocales', {})
    },
    hasPromo: inferHasPromo(raw),
    promoBannerExcludes: ensure(raw, 'promoBannerExcludes', ensure(raw, 'eventPromoBannerExcludes', [])),
    excludes: normalizeCategoryExcludes(raw),
    games: ensure(raw, 'games', ensure(raw, 'eventGames', []))
  }
}

export function parseLoadedConfig (cfg) {
  if (!cfg) {
    return { version: 1, categories: [], excludes: [], customOrdering: [] }
  }

  if (Array.isArray(cfg.categories)) {
    return {
      version: cfg.version ?? 1,
      categories: cfg.categories.map(normalizeCategoryFromApi),
      excludes: Array.isArray(cfg.excludes) ? cfg.excludes.slice() : [],
      customOrdering: Array.isArray(cfg.customOrdering) ? cfg.customOrdering.slice() : []
    }
  }

  if (Array.isArray(cfg.common) && cfg.common.length > 0) {
    return {
      version: 1,
      categories: [migrateLegacyCategory(cfg.common[0])],
      excludes: Array.isArray(cfg.excludes) ? cfg.excludes.slice() : [],
      customOrdering: Array.isArray(cfg.customOrdering) ? cfg.customOrdering.slice() : []
    }
  }

  return { version: 1, categories: [], excludes: [], customOrdering: [] }
}

function resolveFromEnglishLocale (cat, field) {
  const en = cat?.[`${field}Locales`]?.en
  if (en !== undefined && en !== null && String(en).trim() !== '') {
    return String(en)
  }
  return cat?.[field] || ''
}

export function resolveCategoryName (cat) {
  return resolveFromEnglishLocale(cat, 'name')
}

export function resolveCategoryDescription (cat) {
  return resolveFromEnglishLocale(cat, 'description')
}

export function normalizeCategoryOrder (raw) {
  const arr = Array.isArray(raw) ? raw : []
  const toNum = (v) => {
    if (v === '' || v === null || v === undefined) return null
    const num = Number(v)
    return Number.isNaN(num) ? v : num
  }
  const home = toNum(arr[0])
  const topMenu = toNum(arr[1])
  if (home === null && topMenu === null) return []
  if (home === null) return [null, topMenu]
  if (topMenu === null) return [home]
  return [home, topMenu]
}

export function buildCategoryPayload (cat, gamesSlugs) {
  const slugs = gamesSlugs || (cat.games || []).map(g => g.slug)
  const games = slugs.filter(Boolean).map(slug => ({ slug }))
  const order = normalizeCategoryOrder(cat.order)
  return {
    id: cat.id,
    isCategory: true,
    name: resolveCategoryName(cat),
    nameLocales: cat.nameLocales || {},
    description: resolveCategoryDescription(cat),
    descriptionLocales: cat.descriptionLocales || {},
    order,
    startDate: Array.isArray(cat.startDate) ? cat.startDate : [],
    endDate: Array.isArray(cat.endDate) ? cat.endDate : [],
    promoBanner: { ...cat.promoBanner },
    hasPromo: !!cat.hasPromo,
    promoBannerExcludes: cat.hasPromo ? (cat.promoBannerExcludes || []) : [],
    excludes: normalizeCategoryExcludes(cat),
    games
  }
}

export function buildFullPayload ({ version, categories, excludes, customOrdering }) {
  return {
    version: version ?? 1,
    excludes: excludes || [],
    customOrdering: (customOrdering || []).map(item => ({
      pos: item.pos || 0,
      domains: Array.isArray(item.domains) ? item.domains : []
    })),
    categories: (categories || []).map(c => buildCategoryPayload(c))
  }
}

export function validateConfigPayload (payload) {
  const errors = []
  if (!Array.isArray(payload.categories)) {
    errors.push('categories must be an array')
    return errors
  }
  payload.categories.forEach((c, i) => {
    if (!c.id || typeof c.id !== 'string') errors.push(`categories[${i}].id is required`)
    if (typeof c.name !== 'string') errors.push(`categories[${i}].name must be string`)
    if (typeof c.description !== 'string') errors.push(`categories[${i}].description must be string`)
    if (!Array.isArray(c.games)) errors.push(`categories[${i}].games must be an array`)
  })
  const ids = payload.categories.map(c => c.id)
  const dupes = ids.filter((id, idx) => ids.indexOf(id) !== idx)
  if (dupes.length) errors.push(`duplicate category ids: ${[...new Set(dupes)].join(', ')}`)
  return errors
}

export function formatConfigError (e) {
  if (!e) return ''
  if (typeof e === 'string') return e
  if (e.message) return e.message
  if (e.title) return e.title
  if (e.errors) return Object.values(e.errors).flat().join('; ')
  try {
    return JSON.stringify(e)
  } catch (_) {
    return 'Unknown error'
  }
}
